import { useEffect, useState } from "react";
import {
  Container, Title, Text, Card, Button, Group, Stack, SimpleGrid,
  NumberInput, Badge, Center, Loader, ThemeIcon, Alert,
} from "@mantine/core";
import { IconCoins, IconLock } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import { usePlan } from "../hooks/usePlan";
import { getCreditProducts, checkoutCredits } from "./pricingApi";

const MIN_PACKS = 10;
// Preview price only — mirrors VeftCreditOptions.EurPer1000Credits (margin baked in). The actual
// charge is the Stripe price for the pack; tax is added at checkout. Hardcoded € for now (the
// product DTO doesn't carry a currency); the German market bills in EUR.
const EUR_PER_1000_CREDITS = 3;
const eur = (n) => `€${n.toFixed(2)}`;

export default function CreditsPage() {
  const { canBuyCredits, creditsBalance, loaded, plan } = usePlan();
  const [, navigate] = useLocation();
  const [products, setProducts] = useState(null);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!canBuyCredits) return;
    getCreditProducts().then(setProducts).catch(() => setProducts([]));
  }, [canBuyCredits]);

  // Trial (and any plan without buy_credits) can't purchase à la carte — send to /plans.
  if (loaded && !canBuyCredits) {
    return (
      <Container size="sm" py="xl">
        <Alert color="yellow" icon={<IconLock size={18} />} title="Buying credits requires a paid plan">
          <Stack gap="sm">
            <Text size="sm">
              Your {plan} plan can't purchase credit packs. Subscribe to Pro to top up credits whenever you need them.
            </Text>
            <Group>
              <Button onClick={() => navigate("/plans")}>View plans</Button>
            </Group>
          </Stack>
        </Alert>
      </Container>
    );
  }

  const buy = async (product) => {
    const quantity = Math.max(MIN_PACKS, qty[product.name] || MIN_PACKS);
    setBusy(product.name);
    try {
      const res = await checkoutCredits({ items: [{ id: product.name, quantity }] });
      if (res?.redirectUrl) window.location.href = res.redirectUrl;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      notifications.show({ color: "red", title: "Checkout failed", message: String(e.message || e) });
      setBusy(null);
    }
  };

  return (
    <Container size="md" py="xl">
      <Stack align="center" gap="xs" mb="lg">
        <Title order={2}>Buy credits</Title>
        <Text c="dimmed">Credits power enrichment, RHP drafting, search and document generation.</Text>
        {loaded && (
          <Badge size="lg" variant="light" leftSection={<IconCoins size={14} />}>
            Balance: {creditsBalance.toLocaleString()} credits
          </Badge>
        )}
      </Stack>

      {!products && <Center my="xl"><Loader size="sm" /></Center>}

      {products && products.length === 0 && (
        <Text ta="center" c="dimmed">No credit packs are available right now.</Text>
      )}

      {products && products.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: products.length > 1 ? 2 : 1 }} spacing="lg">
          {products.map((p) => {
            const packs = qty[p.name] || MIN_PACKS;
            const unitPrice = ((p.credits ?? 0) / 1000) * EUR_PER_1000_CREDITS;
            return (
              <Card key={p.name} withBorder radius="md" padding="lg">
                <Stack>
                  <Group>
                    <ThemeIcon variant="light" size="lg" radius="md"><IconCoins size={18} /></ThemeIcon>
                    <div>
                      <Text fw={600}>{p.name}</Text>
                      <Text size="sm" c="dimmed">
                        {(p.credits ?? 0).toLocaleString()} credits · {eur(unitPrice)} per pack
                      </Text>
                    </div>
                  </Group>
                  <NumberInput
                    label="Packs"
                    description={`Minimum ${MIN_PACKS}`}
                    min={MIN_PACKS}
                    value={packs}
                    onChange={(v) => setQty((q) => ({ ...q, [p.name]: Number(v) || MIN_PACKS }))}
                  />
                  <Group justify="space-between" align="baseline">
                    <Text size="sm" c="dimmed">{(packs * (p.credits ?? 0)).toLocaleString()} credits</Text>
                    <Text fw={700} size="lg">{eur(unitPrice * packs)}</Text>
                  </Group>
                  <Button loading={busy === p.name} onClick={() => buy(p)}>
                    Buy now
                  </Button>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <Text ta="center" c="dimmed" size="sm" mt="xl">
        Prices exclude VAT, which is added at checkout where applicable. Credits never expire while your subscription is active.
      </Text>
    </Container>
  );
}
